import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Tab,
  Tabs,
  Button,
  CircularProgress,
  LinearProgress,
  alpha,
  Divider,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import {
  Search,
  FilterList,
  TrendingDown,
  Schedule,
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
  Download,
  CloudUpload,
  Description,
  Visibility,
  HourglassEmpty,
  CheckCircleOutline,
  ErrorOutline,
  Email,
  Refresh,
  ContentCopy,
  Lock,
  LocalOffer,
} from '@mui/icons-material';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { getApiUrl, makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { ConfirmDialog, CsvTemplateHint, EmptyState, LoadingState, PageContainer, PageHeader, StatusChip, statusTone, statusLabel } from '../components/ui';
import { mapSheetTemplateProps, mapSheetTemplateParam } from '../utils/mapSheetTemplate';
import { useOrgFeatureFlag, useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import { CorrectRebateDatesDialog, PricingWindowHold, storeHeldSheetActions } from '../components/pricing/CorrectRebateDatesDialog';
import { storeBrandLabel } from '../utils/brandAliases';
import { liveRowStatus } from '../utils/mapPricingRebates';
import { StoreAttestedChip, StoreRebateAttestationBlock, useStoreRebateAttestation } from '../components/StoreRebateAttestation';
import { listStoreRebatePrograms, decideStoreRebateProgram, StoreRebateProgram } from '../services/mapPricingApi';
import { useUrlTab } from '../hooks/useUrlTab';
import { PendingPriceChangesTab, usePendingPriceChangeCount } from '../components/PendingPriceChangesTab';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { Link as RouterLink } from 'react-router-dom';
import { tokens } from '../theme/tokens';
import { UpgradePlansModal } from '../components/UpgradePlansModal';
import { useIsMobile } from '../hooks/useIsMobile';
import { canKeepStorePrivateSheet, PlanLimits } from '../types/billing';
import { StoreRebateRequestsSection } from '../components/rebateRequests/StoreRebateRequestsSection';
import { StoreSaleWindowsSection } from '../components/saleWindows/StoreSaleWindowsSection';

const mobileLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  display: 'block',
};

/** Connected-platform labels for the "applies to all your connected systems" notice. */
const PLATFORM_LABELS: Record<string, string> = {
  shopify: 'Shopify (online)',
  lightspeed: 'Lightspeed (in-store)',
  bigcommerce: 'BigCommerce (online)',
  woocommerce: 'WooCommerce (online)',
};
const platformLabel = (platform?: string) => PLATFORM_LABELS[(platform || '').toLowerCase()] || platform || 'that platform';

interface MasterPricingRecord {
  id: string;
  productId?: string;
  model: string;
  brand: string;
  region: 'US' | 'CA';
  priceType: string;
  category?: string;
  /** Product display name, when the API includes it. The model code is then shown beneath it. */
  productName?: string;
  sellingPrice?: number;
  mapPrice?: number;
  msrp?: number;
  instantRebate?: number;
  tradeCredit?: number;
  dealerPrice?: number;
  /** Set by the API when reimbursement / dealer cost is hidden for an unverified brand */
  vendorPrivateLocked?: boolean;
  sellingPriceAfterIR?: number;
  agreementNumber?: string;
  effectiveDate: Date;
  endDate?: Date;
  status: 'active' | 'upcoming' | 'expired' | 'cancelled';
  createdAt: Date;
  updatedAt: Date;
}

interface PriceHistoryRecord {
  id: string;
  model: string;
  brand: string;
  changeType: string;
  previousValue?: { mapPrice?: number; instantRebate?: number; tradeCredit?: number };
  newValue?: { mapPrice?: number; instantRebate?: number; tradeCredit?: number };
  changedAt: Date;
}

interface Submission {
  id: string;
  windowHold?: PricingWindowHold;
  orgApprovalStatus?: string;
  fileName: string;
  vendor: string;
  region: string;
  status: string;
  approvalScope?: string;
  submitterType?: string;
  recordsFound: number;
  recordsCreated: number;
  recordsUpdated: number;
  errors: string[];
  warnings: string[];
  createdAt: string;
  processedAt?: string;
}

interface SubmissionRecord {
  id: string;
  approvalStatus?: string;
  record: {
    model: string;
    brand: string;
    region: string;
    category?: string;
    mapPrice?: number;
    sellingPrice?: number;
    instantRebate?: number;
    dealerPrice?: number;
    tradeCredit?: number;
    effectiveDate?: string;
    endDate?: string;
  };
  createdAt: string;
}

const getAuthToken = async (): Promise<string> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken(true);
};

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const formatDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString();
};

/**
 * Compact effective range for table cells: "Sep 27 – Oct 4". The year appears
 * only when a date is outside the current year, so the range stays on one line.
 */
const formatDateRange = (start?: Date | string, end?: Date | string): string => {
  if (!start) return '-';
  const thisYear = new Date().getFullYear();
  const fmt = (d: Date) =>
    new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      ...(d.getFullYear() !== thisYear ? { year: 'numeric' as const } : {}),
    }).format(d);
  const startDate = new Date(start);
  if (!end) return fmt(startDate);
  return `${fmt(startDate)} \u2013 ${fmt(new Date(end))}`;
};


// ─── Platform Pricing Tab ───────────────────────────────────────────────────

const PlatformPricingTab: React.FC = () => {
  const isMobile = useIsMobile();

  // Vendors can meet a locked row for another brand; only a store can verify.
  const { userProfile } = useAuth();
  const { getEffectiveUserType } = useImpersonation();
  const isVendorPersona = getEffectiveUserType(userProfile) === 'vendor_supplier';
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);

  const { t } = useAppTranslation();
  // Debounce the search box so typing does not fire a request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchQuery.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const buildParams = (extra: Record<string, string>) => {
    const params = new URLSearchParams(extra);
    if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
    if (brandFilter !== 'all') params.set('brand', brandFilter);
    if (regionFilter !== 'all') params.set('region', regionFilter);
    if (debouncedSearch) params.set('search', debouncedSearch);
    return params;
  };

  // The table is paged on the server: one page of rows per request.
  const { data: pageData, isLoading, error } = useQuery<{ rows: MasterPricingRecord[]; total: number; brandCount: number | null }>({
    queryKey: ['master-pricing', 'page', statusFilter, brandFilter, regionFilter, debouncedSearch, page, rowsPerPage],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = buildParams({ limit: String(rowsPerPage), offset: String(page * rowsPerPage) });
      const response = await fetch(`${getApiUrl()}/map-pricing?${params}`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch pricing records');
      const result = await response.json();
      // `scope.brandCount`: how many brands the store sells (set for a store session only).
      const brandCount = typeof result.scope?.brandCount === 'number' ? result.scope.brandCount : null;
      return { rows: result.data || [], total: Number(result.total ?? result.count ?? 0), brandCount };
    },
  });
  const paginatedRecords = pageData?.rows ?? [];
  const totalRecords = pageData?.total ?? 0;
  const scopeBrandCount = pageData?.brandCount ?? null;

  // Stat cards and the brand list come from a summary of the whole filtered set,
  // fetched once per filter change (not per page) and without row bodies.
  const { data: summary, isLoading: summaryLoading } = useQuery<{ total: number; active: number; withRebates: number; brands: string[]; truncated?: boolean; brandsTruncated?: boolean }>({
    queryKey: ['master-pricing', 'summary', statusFilter, brandFilter, regionFilter, debouncedSearch],
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    queryFn: async () => {
      const params = buildParams({ limit: '1', offset: '0', includeSummary: 'true' });
      const response = await fetch(`${getApiUrl()}/map-pricing?${params}`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch pricing summary');
      const result = await response.json();
      return result.summary;
    },
  });

  const { data: priceHistory } = useQuery<PriceHistoryRecord[]>({
    queryKey: ['price-history', selectedModel],
    queryFn: async () => {
      if (!selectedModel) return [];
      const response = await fetch(`${getApiUrl()}/map-pricing/history?model=${encodeURIComponent(selectedModel)}`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) return [];
      const result = await response.json();
      return result.data || [];
    },
    enabled: !!selectedModel,
  });

  const brands = summary?.brands ?? [];

  // Hide columns that are empty for every row on the page (MSRP, dealer cost, trade credit).
  const showMsrpCol = paginatedRecords.some(r => !!r.msrp && r.msrp > 0);
  const showDealerCol = paginatedRecords.some(r => (!!r.dealerPrice && r.dealerPrice > 0) || r.vendorPrivateLocked);
  const showTradeCol = paginatedRecords.some(r => (!!r.tradeCredit && r.tradeCredit > 0) || r.vendorPrivateLocked);
  const platformColCount = 9 + (showMsrpCol ? 1 : 0) + (showDealerCol ? 1 : 0) + (showTradeCol ? 1 : 0);
  const headNoWrap = { whiteSpace: 'nowrap' as const };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'active': return <StatusChip label="Active" tone="positive" size="small" icon={<CheckCircle />} />;
      case 'upcoming': return <StatusChip label="Upcoming" tone="info" size="small" icon={<Schedule />} />;
      case 'expired': return <StatusChip label="Expired" tone="neutral" size="small" icon={<Cancel />} />;
      default: return <StatusChip label={statusLabel(status)} tone={statusTone(status)} size="small" />;
    }
  };

  const handleExport = async () => {
    const params = buildParams({ limit: '5000', offset: '0' });
    const response = await fetch(`${getApiUrl()}/map-pricing?${params}`, {
      headers: { Authorization: `Bearer ${await getAuthToken()}` },
    });
    if (!response.ok) return;
    const exportRows: MasterPricingRecord[] = (await response.json()).data || [];
    if (!exportRows.length) return;
    const headers = ['Model', 'Brand', 'Region', 'Type', 'MAP Price', 'MSRP', 'Rebate', 'Dealer cost', 'Trade Credit', 'Effective', 'End', 'Status'];
    const rows = exportRows.map(r => [r.model, r.brand, r.region, r.priceType, r.mapPrice ?? '', r.msrp ?? '', r.instantRebate ?? '', r.dealerPrice ?? '', r.tradeCredit ?? '', formatDate(r.effectiveDate), formatDate(r.endDate), liveRowStatus(r)]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `map-pricing-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: showFilters ? 2 : 0 }}>
            <TextField placeholder="Search by model, brand, or category..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} size="small" sx={{ flexGrow: 1, maxWidth: 400 }} InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={(_, v) => {
                if (!v) return;
                setStatusFilter(v);
                setPage(0);
              }}
              size="small"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="active">Active</ToggleButton>
              <ToggleButton value="upcoming">Upcoming</ToggleButton>
              <ToggleButton value="expired">Expired</ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Export CSV"><IconButton onClick={handleExport} disabled={!totalRecords}><Download /></IconButton></Tooltip>
            <IconButton onClick={() => setShowFilters(!showFilters)}><FilterList />{showFilters ? <ExpandLess /> : <ExpandMore />}</IconButton>
          </Stack>
          <Collapse in={showFilters}>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}><InputLabel>Brand</InputLabel><Select value={brandFilter} onChange={(e) => { setBrandFilter(e.target.value); setPage(0); }} label="Brand"><MenuItem value="all">All brands</MenuItem>{brands.map(b => <MenuItem key={b} value={b}>{storeBrandLabel(b)}</MenuItem>)}</Select></FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}><InputLabel>Region</InputLabel><Select value={regionFilter} onChange={(e) => { setRegionFilter(e.target.value); setPage(0); }} label="Region"><MenuItem value="all">All</MenuItem><MenuItem value="US">US</MenuItem><MenuItem value="CA">Canada</MenuItem></Select></FormControl>
            </Stack>
          </Collapse>
        </CardContent>
      </Card>

      {scopeBrandCount !== null && !isVendorPersona && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {brands.length > 0
            ? t('storePricingScope.mapCoverage', { published: brands.length, total: scopeBrandCount })
            : t('storePricingScope.mapCoverageNone', { total: scopeBrandCount })}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {[
          // A capped summary scan is a lower bound: say so rather than show an exact-looking count.
          { value: summary?.total, capped: summary?.truncated, label: 'Records found', color: 'primary' },
          { value: summary?.active, capped: summary?.truncated, label: 'Active pricing', color: 'success.main' },
          { value: summary?.withRebates, capped: summary?.truncated, label: 'With rebates', color: 'info.main' },
          { value: summary ? summary.brands.length : undefined, capped: summary?.brandsTruncated, label: 'Brands with pricing', color: 'warning.main' },
        ].map(({ value, capped, label, color }) => (
          <Box key={label} sx={{ flex: '1 1 160px', minWidth: 160 }}>
            <Card><CardContent sx={{ py: 2 }}>{summaryLoading && value === undefined ? <Skeleton variant="text" width={48} height={36} /> : <Typography variant="h5" color={color}>{value === undefined ? '\u2014' : `${value}${capped ? '+' : ''}`}</Typography>}<Typography variant="body2" color="text.secondary">{label}</Typography></CardContent></Card>
          </Box>
        ))}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>Failed to load pricing data. Please try again.</Alert>}

      <Card>
        {isMobile ? (
          <Box>
            {isLoading ? (
              <Stack spacing={1} sx={{ p: 2 }}>
                {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={88} />)}
              </Stack>
            ) : paginatedRecords.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No pricing records found</Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />}>
                {paginatedRecords.map((record) => {
                  const isSelected = selectedModel === record.model;
                  return (
                    <Box key={record.id}>
                      <Box
                        onClick={() => setSelectedModel(isSelected ? null : record.model)}
                        sx={{ p: 2, cursor: 'pointer' }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="medium">{record.productName || record.model}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[storeBrandLabel(record.brand), record.productName ? record.model : null, record.category].filter(Boolean).join(' · ')}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                            {record.region && <Chip label={record.region} size="small" variant="outlined" />}
                            {getStatusChip(liveRowStatus(record))}
                          </Stack>
                        </Stack>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 1.5 }}>
                          <Box>
                            <Typography component="span" sx={mobileLabelSx}>MAP price</Typography>
                            <Typography variant="body2" fontWeight="medium">{formatCurrency(record.mapPrice)}</Typography>
                          </Box>
                          <Box>
                            <Typography component="span" sx={mobileLabelSx}>MSRP</Typography>
                            <Typography variant="body2">{formatCurrency(record.msrp)}</Typography>
                          </Box>
                          <Box>
                            <Typography component="span" sx={mobileLabelSx}>Rebate</Typography>
                            {record.instantRebate && record.instantRebate > 0 ? (
                              <Chip label={formatCurrency(record.instantRebate)} color="success" size="small" icon={<TrendingDown />} />
                            ) : (
                              <Typography variant="body2">-</Typography>
                            )}
                          </Box>
                          <Box>
                            <Typography component="span" sx={mobileLabelSx}>Effective</Typography>
                            <Typography variant="body2">{formatDate(record.effectiveDate)}</Typography>
                            {record.endDate && <Typography variant="caption" color="text.secondary">to {formatDate(record.endDate)}</Typography>}
                          </Box>
                          <Box>
                            <Typography component="span" sx={mobileLabelSx}>Dealer cost</Typography>
                            {record.dealerPrice && record.dealerPrice > 0 ? (
                              <Typography variant="body2">{formatCurrency(record.dealerPrice)}</Typography>
                            ) : record.vendorPrivateLocked ? (
                              <Tooltip
                                title={
                                  isVendorPersona
                                    ? `Dealer cost is shown to ${record.brand}'s verified dealers.`
                                    : `Dealer cost is shown once your ${record.brand} dealer authorization is verified (Store Settings → Brands).`
                                }
                              >
                                <Chip label="Verified dealers" size="small" variant="outlined" icon={<Lock />} tabIndex={0} />
                              </Tooltip>
                            ) : (
                              <Typography variant="body2">-</Typography>
                            )}
                          </Box>
                          <Box>
                            <Typography component="span" sx={mobileLabelSx}>Trade credit</Typography>
                            {record.tradeCredit && record.tradeCredit > 0 ? (
                              <Chip label={formatCurrency(record.tradeCredit)} color="info" size="small" />
                            ) : record.vendorPrivateLocked ? (
                              <Tooltip
                                title={
                                  isVendorPersona
                                    ? `Dealer reimbursement is shown to ${record.brand}'s verified dealers.`
                                    : `Dealer reimbursement is shown once your ${record.brand} dealer authorization is verified (Store Settings → Brands).`
                                }
                              >
                                <Chip label="Verified dealers" size="small" variant="outlined" icon={<Lock />} tabIndex={0} />
                              </Tooltip>
                            ) : (
                              <Typography variant="body2">-</Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      {isSelected && priceHistory && priceHistory.length > 0 && (
                        <Box sx={{ px: 2, pb: 2, bgcolor: 'action.hover' }}>
                          <Typography variant="subtitle2" gutterBottom>Price history for {record.model}</Typography>
                          <Paper variant="outlined">
                            <Stack divider={<Divider />}>
                              {priceHistory.slice(0, 5).map((h) => (
                                <Box key={h.id} sx={{ p: 1.5 }}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption">{formatDate(h.changedAt)}</Typography>
                                    <Chip label={h.changeType} size="small" variant="outlined" />
                                  </Stack>
                                  <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      MAP {formatCurrency(h.previousValue?.mapPrice)} → {formatCurrency(h.newValue?.mapPrice)}
                                    </Typography>
                                  </Stack>
                                  {h.previousValue?.instantRebate !== h.newValue?.instantRebate && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      Rebate {formatCurrency(h.previousValue?.instantRebate)} → {formatCurrency(h.newValue?.instantRebate)}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          </Paper>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Box>
        ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Model</TableCell><TableCell>Brand</TableCell><TableCell>Region</TableCell><TableCell>Type</TableCell>
                <TableCell align="right" sx={headNoWrap}>MAP price</TableCell>{showMsrpCol && <TableCell align="right" sx={headNoWrap}>MSRP</TableCell>}<TableCell align="right" sx={headNoWrap}>Rebate</TableCell>{showDealerCol && <TableCell align="right" sx={headNoWrap}>Dealer cost</TableCell>}{showTradeCol && <TableCell align="right" sx={headNoWrap}>Trade credit</TableCell>}
                <TableCell sx={headNoWrap}>Effective</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? [...Array(8)].map((_, i) => <TableRow key={i}>{[...Array(platformColCount)].map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>) :
               paginatedRecords.length === 0 ? <TableRow><TableCell colSpan={platformColCount} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No pricing records found</Typography></TableCell></TableRow> :
               paginatedRecords.map(record => (
                <React.Fragment key={record.id}>
                  <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedModel(selectedModel === record.model ? null : record.model)}>
                    <TableCell><Typography variant="body2" fontWeight="medium">{record.productName || record.model}</Typography>{(record.productName || record.category) && <Typography variant="caption" color="text.secondary">{[record.productName ? record.model : null, record.category].filter(Boolean).join(' · ')}</Typography>}</TableCell>
                    <TableCell>{storeBrandLabel(record.brand)}</TableCell>
                    <TableCell>{record.region ? <Chip label={record.region} size="small" variant="outlined" /> : '-'}</TableCell>
                    <TableCell>{record.priceType || '-'}</TableCell>
                    <TableCell align="right"><Typography fontWeight="medium">{formatCurrency(record.mapPrice)}</Typography></TableCell>
                    {showMsrpCol && <TableCell align="right">{formatCurrency(record.msrp)}</TableCell>}
                    <TableCell align="right">{record.instantRebate && record.instantRebate > 0 ? <Chip label={formatCurrency(record.instantRebate)} color="success" size="small" icon={<TrendingDown />} /> : '-'}</TableCell>
                    {showDealerCol && <TableCell align="right">
                      {record.dealerPrice && record.dealerPrice > 0 ? (
                        formatCurrency(record.dealerPrice)
                      ) : record.vendorPrivateLocked ? (
                        <Tooltip
                          title={
                            isVendorPersona
                              ? `Dealer cost is shown to ${record.brand}'s verified dealers.`
                              : `Dealer cost is shown once your ${record.brand} dealer authorization is verified (Store Settings → Brands).`
                          }
                        >
                          <Chip label="Verified dealers" size="small" variant="outlined" icon={<Lock />} tabIndex={0} />
                        </Tooltip>
                      ) : '-'}
                    </TableCell>}
                    {showTradeCol && <TableCell align="right">
                      {record.tradeCredit && record.tradeCredit > 0 ? (
                        <Chip label={formatCurrency(record.tradeCredit)} color="info" size="small" />
                      ) : record.vendorPrivateLocked ? (
                        <Tooltip
                          title={
                            isVendorPersona
                              ? `Dealer reimbursement is shown to ${record.brand}'s verified dealers.`
                              : `Dealer reimbursement is shown once your ${record.brand} dealer authorization is verified (Store Settings → Brands).`
                          }
                        >
                          <Chip label="Verified dealers" size="small" variant="outlined" icon={<Lock />} tabIndex={0} />
                        </Tooltip>
                      ) : '-'}
                    </TableCell>}
                    <TableCell sx={headNoWrap}><Typography variant="body2" noWrap>{formatDateRange(record.effectiveDate, record.endDate)}</Typography></TableCell>
                    <TableCell>{getStatusChip(liveRowStatus(record))}</TableCell>
                  </TableRow>
                  {selectedModel === record.model && priceHistory && priceHistory.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={platformColCount} sx={{ bgcolor: 'action.hover', p: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>Price history for {record.model}</Typography>
                        <Table size="small">
                          <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Change</TableCell><TableCell align="right">Previous MAP</TableCell><TableCell align="right">New MAP</TableCell><TableCell align="right">Rebate change</TableCell></TableRow></TableHead>
                          <TableBody>
                            {priceHistory.slice(0, 5).map(h => (
                              <TableRow key={h.id}>
                                <TableCell>{formatDate(h.changedAt)}</TableCell>
                                <TableCell><Chip label={h.changeType} size="small" variant="outlined" /></TableCell>
                                <TableCell align="right">{formatCurrency(h.previousValue?.mapPrice)}</TableCell>
                                <TableCell align="right">{formatCurrency(h.newValue?.mapPrice)}</TableCell>
                                <TableCell align="right">{h.previousValue?.instantRebate !== h.newValue?.instantRebate ? <>{formatCurrency(h.previousValue?.instantRebate)} → {formatCurrency(h.newValue?.instantRebate)}</> : '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
        <TablePagination rowsPerPageOptions={[10, 25, 50, 100]} component="div" count={totalRecords} rowsPerPage={rowsPerPage} page={page} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
      </Card>
    </>
  );
};

// ─── My Uploads Tab ────────────────────────────────────────────────────────────────────

const MyUploadsTab: React.FC = () => {
  const { t } = useAppTranslation();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [expandedSubmission, setExpandedSubmission] = useState<string | null>(null);
  // Where the sheet applies: every location, one location, or one integration.
  const [pricingScope, setPricingScope] = useState('all');
  // "Keep this pricing for my store only" — the same rule as the server's
  // assertStorePrivateSheetAllowed (canKeepStorePrivateSheet).
  const [privateToStore, setPrivateToStore] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  // Flag store_attested_rebates: a store-private sheet says what it is; the upload waits for the confirmation.
  const attestation = useStoreRebateAttestation(privateToStore);

  // flags is null while loading and when /feature-flags/me failed: both leave
  // the ladder unknown rather than reading it as off.
  const { flags: orgFlags } = useOrgFeatureFlags();
  const ladderEnabled = orgFlags?.variable_pricing_ladder === true;
  const { data: subscription } = useQuery<{ plan?: { id: string; limits?: Pick<PlanLimits, 'customPricing' | 'variablePricingLevel'> } }>({
    queryKey: ['my-subscription'],
    queryFn: async () => {
      const res = await makeApiCall('/billing/my-subscription', { headers: await getAuthHeaders() });
      if (!res.ok) return {};
      return (await res.json()).data || {};
    },
  });
  // Undefined while the plan or the flags are still loading (or either
  // fetch failed): the checkbox is not blocked then and the server gate
  // decides, rather than upselling a store a rung its plan already includes.
  const planLimits = subscription?.plan?.limits;
  const canKeepPrivate =
    planLimits != null && orgFlags != null ? canKeepStorePrivateSheet(planLimits, ladderEnabled) : undefined;

  const { data: scopeLocations } = useQuery<Array<{ id: string; name: string; isPrimary: boolean; isActive: boolean }>>({
    queryKey: ['store-locations'],
    queryFn: async () => {
      // makeApiCall carries the impersonation headers, so an admin viewing a store sees its locations
      const res = await makeApiCall('/store/locations', { headers: await getAuthHeaders() });
      if (!res.ok) return [];
      return (await res.json()).data || [];
    },
  });
  const { data: scopeIntegrations } = useQuery<Array<{ id: string; platform?: string; storeName?: string; name?: string }>>({
    queryKey: ['integrations'],
    queryFn: async () => {
      const res = await makeApiCall('/integrations', { headers: await getAuthHeaders() });
      if (!res.ok) return [];
      return (await res.json()).data || [];
    },
  });

  const { data: submissions, isLoading: loadingSubmissions } = useQuery<Submission[]>({
    queryKey: ['my-pricing-submissions'],
    queryFn: async () => {
      const res = await makeApiCall(`/pricing-ingestion/submissions?limit=50`, { headers: await getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json();
      return json.data || [];
    },
  });

  const { data: submissionRecords } = useQuery<SubmissionRecord[]>({
    queryKey: ['submission-records', expandedSubmission],
    queryFn: async () => {
      if (!expandedSubmission) return [];
      const res = await makeApiCall(`/pricing-ingestion/submissions/${expandedSubmission}/records`, { headers: await getAuthHeaders() });
      if (!res.ok) return [];
      const json = await res.json();
      return json.data || [];
    },
    enabled: !!expandedSubmission,
  });

  const { enabled: canCorrectDates } = useOrgFeatureFlag('pricing_window_correction');
  const [correctingSubmission, setCorrectingSubmission] = useState<Submission | null>(null);
  const [approvingHeld, setApprovingHeld] = useState<Submission | null>(null);
  const approveHeld = useMutation({
    mutationFn: async (submissionId: string) => {
      const res = await makeApiCall(`/pricing-ingestion/submissions/${submissionId}/org-approve`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.message || 'Approval failed');
      }
    },
    onSuccess: () => {
      setApprovingHeld(null);
      queryClient.invalidateQueries({ queryKey: ['my-pricing-submissions'] });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const params = new URLSearchParams({ scope: 'organization', pricingScope });
      // A CSV in the downloadable template layout is read by the canonical sheet parser.
      const templateParam = await mapSheetTemplateParam(file);
      if (templateParam) params.set('template', templateParam);
      if (privateToStore) params.set('privateToStore', 'true');
      if (attestation.payload) formData.append('attestation', attestation.payload);
      const res = await makeApiCall(`/pricing-ingestion/upload?${params.toString()}`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error?.message || 'Upload failed');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-pricing-submissions'] });
    },
  });

  const handleFiles = useCallback((files: FileList | File[]) => {
    if (attestation.blocked) return;
    const allowed = ['.xlsx', '.xlsm', '.xls', '.csv', '.pdf'];
    for (const file of Array.from(files)) {
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!allowed.includes(ext)) continue;
      uploadMutation.mutate(file);
    }
  }, [uploadMutation, attestation.blocked]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const getSubmissionStatusChip = (status: string) => {
    switch (status) {
      case 'pending': return <StatusChip label="Pending approval" tone="caution" size="small" icon={<HourglassEmpty />} />;
      case 'approved': case 'processed': return <StatusChip label="Approved" tone="positive" size="small" icon={<CheckCircleOutline />} />;
      case 'rejected': return <StatusChip label="Rejected" tone="problem" size="small" icon={<ErrorOutline />} />;
      case 'duplicate': return <StatusChip label="Duplicate" tone="caution" size="small" />;
      default: return <StatusChip label={statusLabel(status)} tone={statusTone(status)} size="small" />;
    }
  };

  return (
    <>
      {/* Where the next upload applies */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel id="pricing-scope-label">This sheet applies to</InputLabel>
          <Select labelId="pricing-scope-label" label="This sheet applies to" value={pricingScope} onChange={(e) => setPricingScope(e.target.value)}>
            <MenuItem value="all">All locations and integrations</MenuItem>
            {(scopeLocations || []).filter((l) => l.isActive).map((l) => (
              <MenuItem key={l.id} value={`location:${l.id}`}>Location · {l.name}{l.isPrimary ? ' (primary)' : ''}</MenuItem>
            ))}
            {(scopeIntegrations || []).map((i) => (
              <MenuItem key={i.id} value={`integration:${i.id}`}>Integration · {(i.platform || 'integration')}{i.storeName || i.name ? ` · ${i.storeName || i.name}` : ''}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">
          A sheet for one location or integration is only used when pricing that location or integration, and outranks an all-locations sheet there. {ladderEnabled
            ? 'One location needs Advanced variable pricing; one integration needs Complete.'
            : 'Scoping needs a plan with custom pricing.'}
        </Typography>
      </Stack>

      {/* Keep this pricing private to the store */}
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={privateToStore}
              onChange={(e) => {
                if (e.target.checked && canKeepPrivate === false) {
                  setUpgradeModalOpen(true);
                  return;
                }
                setPrivateToStore(e.target.checked);
              }}
            />
          }
          label="Keep this pricing for my store only"
        />
        {!privateToStore && (
          <Box sx={{ pl: 4 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              This pricing applies to all your connected systems, including your online store if you connect one.
              {(scopeIntegrations || []).length > 0
                ? ` Today it applies to ${(scopeIntegrations || []).map((i) => platformLabel(i.platform)).join(', ')}.`
                : ''}
            </Typography>
            {(scopeIntegrations || []).length === 1 && (
              <Typography variant="caption" color="text.secondary" display="block">
                Today this only reaches {platformLabel((scopeIntegrations || [])[0]?.platform)}. When you connect an online store it will price there too.
                Keep in-store pricing separate with{' '}
                <Link component="button" type="button" onClick={() => setUpgradeModalOpen(true)}>Custom pricing</Link>.
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {attestation.visible && <StoreRebateAttestationBlock draft={attestation.draft} onChange={attestation.update} />}

      {/* Upload Area */}
      <Card sx={{ mb: 3, border: '2px dashed', borderColor: dragging && !attestation.blocked ? 'primary.main' : 'divider', opacity: attestation.blocked ? 0.6 : 1, bgcolor: (theme) => (dragging ? alpha(theme.palette.primary.main, 0.04) : 'transparent'), transition: 'all 0.2s' }}>
        <CardContent
          sx={{ py: 4, textAlign: 'center', cursor: attestation.blocked ? 'not-allowed' : 'pointer' }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => { if (!attestation.blocked) fileInputRef.current?.click(); }}
        >
          <input ref={fileInputRef} type="file" hidden disabled={attestation.blocked} multiple accept=".xlsx,.xlsm,.xls,.csv,.pdf" onChange={(e) => e.target.files && handleFiles(e.target.files)} />
          {uploadMutation.isPending ? (
            <LoadingState message="Processing file..." />
          ) : (
            <Box>
              <CloudUpload sx={{ fontSize: 48, color: dragging ? 'primary.main' : 'text.disabled', mb: 1 }} />
              <Typography variant="body1" fontWeight={500}>
                Drag & drop MAP pricing or rebate files here
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                or click to browse — Supports .xlsx, .xlsm, .xls, .csv, .pdf
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ mb: 3 }}>
        <CsvTemplateHint {...mapSheetTemplateProps(t, 'mapSheet')} />
        {attestation.visible && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {t('store.attestedRebates.templateNote')}
          </Typography>
        )}
      </Box>

      {/* Email forwarding tip */}
      <Alert
        severity="info"
        icon={<Email />}
        sx={{ mb: 3, borderRadius: 1.5, '& .MuiAlert-message': { width: '100%' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="body2" fontWeight={600}>Forward pricing emails for automatic ingestion</Typography>
            <Typography variant="body2" color="text.secondary">
              Set up email forwarding in the "Email ingestion" tab to automatically parse MAP pricing and rebate attachments.
            </Typography>
          </Box>
        </Box>
      </Alert>

      {uploadMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 1 }}>
          File uploaded successfully — {uploadMutation.data?.data?.recordsFound || 0} records found.
          {uploadMutation.data?.data?.status === 'pending' && ' Pending admin approval for platform distribution.'}
        </Alert>
      )}
      {uploadMutation.isError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
          {(uploadMutation.error as Error)?.message || 'Upload failed'}
        </Alert>
      )}

      <CorrectRebateDatesDialog
        submission={correctingSubmission}
        approveAction="org-approve"
        invalidateKeys={['my-pricing-submissions']}
        onClose={() => setCorrectingSubmission(null)}
      />

      <ConfirmDialog
        open={!!approvingHeld}
        title={approvingHeld?.windowHold?.status === 'open' ? 'Approve with these dates?' : 'Approve the corrected dates?'}
        message={
          <>
            {approvingHeld?.windowHold?.status === 'open'
              ? approvingHeld.windowHold.message
              : `${approvingHeld?.fileName} will start pricing with the corrected rebate dates.`}
            {approveHeld.error && (
              <Alert severity="error" sx={{ mt: 2 }}>{(approveHeld.error as Error).message}</Alert>
            )}
          </>
        }
        confirmLabel="Approve"
        loading={approveHeld.isPending}
        onConfirm={() => approvingHeld && approveHeld.mutate(approvingHeld.id)}
        onClose={() => setApprovingHeld(null)}
      />

      {/* Submissions History */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Upload history</Typography>

      {loadingSubmissions ? (
        <Stack spacing={1}>{[...Array(3)].map((_, i) => <Skeleton key={i} variant="rounded" height={64} />)}</Stack>
      ) : !submissions || submissions.length === 0 ? (
        <Card><CardContent>
          <EmptyState
            size="compact"
            icon={<Description />}
            title="No uploads yet"
            description="Upload a MAP pricing or rebate file to get started."
          />
        </CardContent></Card>
      ) : (
        <Stack spacing={1.5}>
          {submissions.map(sub => (
            <Card key={sub.id} sx={{ borderRadius: 1.5 }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                    <Description sx={{ color: 'text.secondary', fontSize: 20 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{sub.fileName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sub.vendor} · {sub.region} · {sub.recordsFound} records · {formatDate(sub.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {sub.windowHold?.status === 'open' && (
                      <StatusChip label="Dates need checking" tone="caution" size="small" />
                    )}
                    {storeHeldSheetActions(sub, canCorrectDates).correct && (
                      <Button size="small" variant="outlined" onClick={() => setCorrectingSubmission(sub)}>
                        Correct dates
                      </Button>
                    )}
                    {(storeHeldSheetActions(sub, canCorrectDates).approveAsIs || storeHeldSheetActions(sub, canCorrectDates).approveCorrected) && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          approveHeld.reset();
                          setApprovingHeld(sub);
                        }}
                      >
                        {storeHeldSheetActions(sub, canCorrectDates).approveAsIs ? 'Approve as is' : 'Approve'}
                      </Button>
                    )}
                    {getSubmissionStatusChip(sub.status)}
                    {sub.approvalScope === 'organization' && <Chip label="Local" size="small" variant="outlined" />}
                    <Tooltip title="View records">
                      <IconButton size="small" onClick={() => setExpandedSubmission(expandedSubmission === sub.id ? null : sub.id)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>

                {sub.warnings?.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 1, py: 0, borderRadius: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                    {sub.warnings.slice(0, 2).join('; ')}
                    {sub.warnings.length > 2 && ` (+${sub.warnings.length - 2} more)`}
                  </Alert>
                )}
                {sub.errors?.length > 0 && (
                  <Alert severity="error" sx={{ mt: 1, py: 0, borderRadius: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                    {sub.errors.slice(0, 2).join('; ')}
                  </Alert>
                )}

                <Collapse in={expandedSubmission === sub.id}>
                  <Divider sx={{ my: 1.5 }} />
                  {!submissionRecords || submissionRecords.length === 0 ? (
                    <EmptyState size="compact" title="No parsed records available" />
                  ) : isMobile ? (
                    <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                      <Stack divider={<Divider />}>
                        {submissionRecords.map((rec) => (
                          <Box key={rec.id} sx={{ p: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={500}>{rec.record.model}</Typography>
                                <Typography variant="caption" color="text.secondary">{rec.record.brand} · {rec.record.region}</Typography>
                              </Box>
                              {rec.approvalStatus === 'approved'
                                ? <StatusChip label="Approved" tone="positive" size="small" />
                                : rec.approvalStatus === 'rejected'
                                  ? <StatusChip label="Rejected" tone="problem" size="small" />
                                  : <StatusChip label="Pending" tone="caution" size="small" />}
                            </Stack>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 1 }}>
                              <Box>
                                <Typography component="span" sx={mobileLabelSx}>MAP price</Typography>
                                <Typography variant="body2">{formatCurrency(rec.record.mapPrice || rec.record.sellingPrice)}</Typography>
                              </Box>
                              <Box>
                                <Typography component="span" sx={mobileLabelSx}>Rebate</Typography>
                                <Typography variant="body2">{formatCurrency(rec.record.instantRebate)}</Typography>
                              </Box>
                              <Box>
                                <Typography component="span" sx={mobileLabelSx}>Dealer cost</Typography>
                                <Typography variant="body2">{formatCurrency(rec.record.dealerPrice)}</Typography>
                              </Box>
                              <Box>
                                <Typography component="span" sx={mobileLabelSx}>Trade credit</Typography>
                                <Typography variant="body2">{formatCurrency(rec.record.tradeCredit)}</Typography>
                              </Box>
                              <Box>
                                <Typography component="span" sx={mobileLabelSx}>Effective</Typography>
                                <Typography variant="body2">{formatDate(rec.record.effectiveDate)}</Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  ) : (
                    <TableContainer sx={{ maxHeight: 300 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Model</TableCell><TableCell>Brand</TableCell><TableCell>Region</TableCell>
                            <TableCell align="right">MAP price</TableCell><TableCell align="right">Rebate</TableCell><TableCell align="right">Dealer cost</TableCell><TableCell align="right">Trade credit</TableCell>
                            <TableCell>Effective</TableCell><TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {submissionRecords.map(rec => (
                            <TableRow key={rec.id}>
                              <TableCell><Typography variant="body2" fontWeight={500}>{rec.record.model}</Typography></TableCell>
                              <TableCell>{rec.record.brand}</TableCell>
                              <TableCell>{rec.record.region}</TableCell>
                              <TableCell align="right">{formatCurrency(rec.record.mapPrice || rec.record.sellingPrice)}</TableCell>
                              <TableCell align="right">{formatCurrency(rec.record.instantRebate)}</TableCell>
                              <TableCell align="right">{formatCurrency(rec.record.dealerPrice)}</TableCell>
                              <TableCell align="right">{formatCurrency(rec.record.tradeCredit)}</TableCell>
                              <TableCell>{formatDate(rec.record.effectiveDate)}</TableCell>
                              <TableCell>
                                {rec.approvalStatus === 'approved'
                                  ? <StatusChip label="Approved" tone="positive" size="small" />
                                  : rec.approvalStatus === 'rejected'
                                    ? <StatusChip label="Rejected" tone="problem" size="small" />
                                    : <StatusChip label="Pending" tone="caution" size="small" />}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Collapse>
              </CardContent>
              {uploadMutation.isPending && <LinearProgress />}
            </Card>
          ))}
        </Stack>
      )}

      <UpgradePlansModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        currentPlanId={subscription?.plan?.id || 'free-preview'}
      />
    </>
  );
};

// ─── Email Ingestion Tab ─────────────────────────────────────────────────────────────────

interface EmailIngestionRecord {
  id: string;
  emailId: string;
  from: string;
  subject: string;
  receivedAt: string;
  attachmentsProcessed: number;
  recordsParsed: number;
  recordsSaved: number;
  errors: string[];
  status: 'success' | 'partial' | 'error';
  processedAt: string;
}

const EmailIngestionTab: React.FC = () => {
  const isMobile = useIsMobile();
  const [copied, setCopied] = useState(false);
  const queryClient = useQueryClient();

  const { data: addressData, isLoading: loadingAddress } = useQuery<{
    forwardingAddress: string;
    organizationId: string;
    isActive: boolean;
  }>({
    queryKey: ['email-map-address'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/email-map/address`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch address');
      const result = await response.json();
      return result;
    },
  });

  const { data: history, isLoading: loadingHistory } = useQuery<EmailIngestionRecord[]>({
    queryKey: ['email-map-history'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/email-map/history?limit=20`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch history');
      const result = await response.json();
      return result.history || [];
    },
  });

  const { data: stats } = useQuery<{
    totalEmailsProcessed: number;
    totalRecordsSaved: number;
    totalErrors: number;
    successRate: string;
  }>({
    queryKey: ['email-map-stats'],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl()}/email-map/stats`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      const result = await response.json();
      return result.stats;
    },
  });

  const checkNowMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${getApiUrl()}/email-map/check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${await getAuthToken()}` },
      });
      if (!response.ok) throw new Error('Check failed');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-map-history'] });
      queryClient.invalidateQueries({ queryKey: ['email-map-stats'] });
    },
  });

  const handleCopy = () => {
    if (addressData?.forwardingAddress) {
      navigator.clipboard.writeText(addressData.forwardingAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      {/* Forwarding Address Card */}
      <Card sx={{ mb: 3, borderRadius: 1.5 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Email color="primary" />
            <Typography variant="h6" fontWeight={600}>Email ingestion</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Forward MAP pricing or rebate emails with Excel/CSV/PDF attachments to the address below.
            They'll be automatically parsed and ingested within 5 minutes.
          </Typography>

          {loadingAddress ? (
            <Skeleton variant="rounded" height={48} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{ color: 'primary.main', flex: 1, userSelect: 'all', fontFamily: tokens.font.mono }}
              >
                {addressData?.forwardingAddress || 'Loading...'}
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy address'}>
                <IconButton size="small" onClick={handleCopy}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={checkNowMutation.isPending ? <CircularProgress size={16} /> : <Refresh />}
              onClick={() => checkNowMutation.mutate()}
              disabled={checkNowMutation.isPending}
            >
              {checkNowMutation.isPending ? 'Checking...' : 'Check now'}
            </Button>
            {checkNowMutation.isSuccess && (
              <Typography variant="body2" color="success.main">
                {checkNowMutation.data?.emailsProcessed || 0} emails processed
              </Typography>
            )}
            {checkNowMutation.isError && (
              <Typography variant="body2" color="error.main">
                {(checkNowMutation.error as Error)?.message || 'Check failed'}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Stats */}
      {stats && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Card sx={{ flex: '1 1 140px', borderRadius: 1.5 }}>
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700}>{stats.totalEmailsProcessed}</Typography>
              <Typography variant="caption" color="text.secondary">Emails processed</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 140px', borderRadius: 1.5 }}>
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700}>{stats.totalRecordsSaved}</Typography>
              <Typography variant="caption" color="text.secondary">Records saved</Typography>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 140px', borderRadius: 1.5 }}>
            <CardContent sx={{ py: 1.5, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700}>{stats.successRate}</Typography>
              <Typography variant="caption" color="text.secondary">Success rate</Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* History */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Ingestion history</Typography>

      {loadingHistory ? (
        <Stack spacing={1}>{[...Array(3)].map((_, i) => <Skeleton key={i} variant="rounded" height={56} />)}</Stack>
      ) : !history || history.length === 0 ? (
        <Card><CardContent>
          <EmptyState
            size="compact"
            icon={<Email />}
            title="No emails processed yet"
            description="Forward a pricing email to get started."
          />
        </CardContent></Card>
      ) : isMobile ? (
        <Paper variant="outlined" sx={{ borderRadius: 1.5 }}>
          <Stack divider={<Divider />}>
            {history.map((record) => (
              <Box key={record.id} sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{record.from}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {record.subject}
                    </Typography>
                  </Box>
                  <StatusChip
                    label={statusLabel(record.status)}
                    size="small"
                    tone={record.status === 'success' ? 'positive' : record.status === 'partial' ? 'caution' : 'problem'}
                  />
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mt: 1.5 }}>
                  <Box>
                    <Typography component="span" sx={mobileLabelSx}>Attachments</Typography>
                    <Typography variant="body2">{record.attachmentsProcessed}</Typography>
                  </Box>
                  <Box>
                    <Typography component="span" sx={mobileLabelSx}>Records</Typography>
                    <Typography variant="body2">{record.recordsSaved}</Typography>
                  </Box>
                  <Box>
                    <Typography component="span" sx={mobileLabelSx}>Date</Typography>
                    <Typography variant="body2">{formatDate(record.processedAt)}</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>From</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell align="center">Attachments</TableCell>
                <TableCell align="center">Records</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map(record => (
                <TableRow key={record.id}>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                      {record.from}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                      {record.subject}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">{record.attachmentsProcessed}</TableCell>
                  <TableCell align="center">{record.recordsSaved}</TableCell>
                  <TableCell>
                    <StatusChip
                      label={statusLabel(record.status)}
                      size="small"
                      tone={record.status === 'success' ? 'positive' : record.status === 'partial' ? 'caution' : 'problem'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatDate(record.processedAt)}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Instructions */}
      <Alert severity="info" sx={{ mt: 3, borderRadius: 1.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>Supported formats</Typography>
        <Typography variant="body2" color="text.secondary">
          Excel (.xlsx, .xlsm, .xls), CSV, and PDF attachments are processed automatically.
          Include "MAP", "pricing", or "rebate" in the subject line for best results.
          Files from ProMaster, Sony, Canon, and Nikon are auto-detected by filename.
        </Typography>
      </Alert>
    </>
  );
};

// ─── Vendor Offers Tab ──────────────────────────────────────────────────────────────────────

interface VendorOffer {
  id: string;
  vendor: string;
  appliesTo?: string;
  targetBrandName: string | null;
  fileName: string;
  status: string;
  orgApprovalStatus: string;
  recordsFound: number;
  rejectionReason: string | null;
  createdAt: string | null;
  orgApprovedAt: string | null;
}

// ─── Rebate programs (flag: rebate_programs) ─────────────────────────────────────────────────────

const REBATE_KIND_LABEL: Record<StoreRebateProgram['kind'], string> = {
  instant_rebate: 'Instant rebate',
  trade_credit: 'Trade credit',
  bundle: 'Bundle offer',
};

const rebateProgramChip = (status: StoreRebateProgram['status']) => {
  if (status === 'active') return <StatusChip label="Active" tone="positive" size="small" icon={<CheckCircleOutline />} />;
  if (status === 'declined') return <StatusChip label="Declined" tone="problem" size="small" icon={<ErrorOutline />} />;
  if (status === 'expired') return <StatusChip label="Expired" tone="neutral" size="small" />;
  if (status === 'draft') return <StatusChip label="Draft" tone="neutral" size="small" />;
  return <StatusChip label="Waiting for you" tone="caution" size="small" icon={<HourglassEmpty />} />;
};

/**
 * Rebate programs that apply to this store: one brand's offer for a window,
 * for every location or for the one location / integration it names. A
 * program's rows do not price anything until the store accepts it here.
 */
const RebateProgramsSection: React.FC = () => {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { enabled } = useOrgFeatureFlag('rebate_programs');
  const [error, setError] = useState<string | null>(null);

  const { data: programs, isLoading, isError, error: loadError, refetch } = useQuery<StoreRebateProgram[]>({
    queryKey: ['store-rebate-programs'],
    queryFn: () => listStoreRebatePrograms(),
    enabled,
  });

  const { data: locations } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['store-locations'],
    queryFn: async () => {
      const res = await makeApiCall('/store/locations', { headers: await getAuthHeaders() });
      if (!res.ok) return [];
      return (await res.json()).data || [];
    },
    enabled,
  });

  const { data: integrations } = useQuery<Array<{ id: string; platform?: string; storeName?: string; name?: string }>>({
    queryKey: ['integrations'],
    queryFn: async () => {
      const res = await makeApiCall('/integrations', { headers: await getAuthHeaders() });
      if (!res.ok) return [];
      return (await res.json()).data || [];
    },
    enabled,
  });

  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'decline' }) => decideStoreRebateProgram(id, action),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['store-rebate-programs'] });
    },
    onError: (e: any) => setError(e?.response?.data?.error?.message || e?.message || 'Request failed'),
  });

  if (!enabled) return null;

  const scopeLabel = (scope: StoreRebateProgram['scope']): string => {
    if (!scope || scope.kind === 'all') return 'All locations';
    if (scope.kind === 'location') return locations?.find((l) => l.id === scope.locationId)?.name || 'One location';
    const integration = integrations?.find((i) => i.id === scope.integrationId);
    return integration?.storeName || integration?.name || integration?.platform || 'One integration';
  };

  const window = (program: StoreRebateProgram): string => {
    const start = program.startAt ? new Date(program.startAt).toLocaleDateString() : '—';
    const end = program.endAt ? new Date(program.endAt).toLocaleDateString() : 'open';
    return `${start} – ${end}`;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Rebate programs</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}
        >
          {(loadError as any)?.response?.data?.error?.message || (loadError as any)?.message || 'Could not load rebate programs.'}
        </Alert>
      ) : !programs || programs.length === 0 ? (
        <EmptyState title="No rebate programs" description="Rebates from your sheets and from your vendors show up here to accept." size="compact" />
      ) : isMobile ? (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Stack divider={<Divider />}>
            {programs.map((program) => (
              <Box key={program.id} sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2">{program.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{program.brand} · {REBATE_KIND_LABEL[program.kind]}</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    {rebateProgramChip(program.status)}
                    <StoreAttestedChip attestation={program.storeAttestation} />
                  </Stack>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 1.5 }}>
                  <Box>
                    <Typography component="span" sx={mobileLabelSx}>Applies to</Typography>
                    <Typography variant="body2">{scopeLabel(program.scope)}</Typography>
                  </Box>
                  <Box>
                    <Typography component="span" sx={mobileLabelSx}>Runs</Typography>
                    <Typography variant="body2">{window(program)}</Typography>
                  </Box>
                </Box>
                {program.status === 'pending_store_approval' && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" variant="contained" disabled={decide.isPending} onClick={() => decide.mutate({ id: program.id, action: 'approve' })}>Approve</Button>
                    <Button size="small" color="inherit" variant="outlined" disabled={decide.isPending} onClick={() => decide.mutate({ id: program.id, action: 'decline' })}>Decline</Button>
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Program</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Applies to</TableCell>
                <TableCell>Runs</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Decision</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {programs.map((program) => (
                <TableRow key={program.id} hover>
                  <TableCell>{program.name}</TableCell>
                  <TableCell>{program.brand}</TableCell>
                  <TableCell>{REBATE_KIND_LABEL[program.kind]}</TableCell>
                  <TableCell>{scopeLabel(program.scope)}</TableCell>
                  <TableCell>{window(program)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                      {rebateProgramChip(program.status)}
                      <StoreAttestedChip attestation={program.storeAttestation} />
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    {program.status === 'pending_store_approval' && (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button size="small" variant="contained" disabled={decide.isPending} onClick={() => decide.mutate({ id: program.id, action: 'approve' })}>Approve</Button>
                        <Button size="small" color="inherit" disabled={decide.isPending} onClick={() => decide.mutate({ id: program.id, action: 'decline' })}>Decline</Button>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

/**
 * Jordan's side of private dealer pricing: sheets a vendor sent to this
 * store. Accepting one makes its rows this store's pricing for those
 * products (above the store's own uploaded sheets, below per-product
 * overrides). Nothing applies until accepted.
 */
const VendorOffersTab: React.FC = () => {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: offers, isLoading } = useQuery<VendorOffer[]>({
    queryKey: ['vendor-offers'],
    queryFn: async () => {
      const res = await makeApiCall(`/pricing-ingestion/offers`, { headers: await getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to load');
      return (await res.json()).data || [];
    },
  });

  const { data: offerRecords } = useQuery<SubmissionRecord[]>({
    queryKey: ['submission-records', expanded],
    queryFn: async () => {
      if (!expanded) return [];
      const res = await makeApiCall(`/pricing-ingestion/submissions/${expanded}/records`, { headers: await getAuthHeaders() });
      if (!res.ok) return [];
      return (await res.json()).data || [];
    },
    enabled: !!expanded,
  });

  const decide = useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'org-approve' | 'org-decline' }) => {
      const res = await makeApiCall(`/pricing-ingestion/submissions/${id}/${action}`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || body?.message || 'Request failed');
      }
      return res.json();
    },
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['vendor-offers'] });
      queryClient.invalidateQueries({ queryKey: ['my-pricing-submissions'] });
    },
    onError: (e: any) => setError(e?.message || 'Request failed'),
  });

  const offerChip = (offer: VendorOffer) => {
    if (offer.orgApprovalStatus === 'approved') return <StatusChip label="Accepted" tone="positive" size="small" icon={<CheckCircleOutline />} />;
    if (offer.status === 'rejected') return <StatusChip label={offer.rejectionReason === 'Withdrawn by the vendor' ? 'Withdrawn' : 'Declined'} tone="problem" size="small" icon={<ErrorOutline />} />;
    if (offer.status === 'duplicate') return <StatusChip label="Duplicate" tone="caution" size="small" />;
    return <StatusChip label="Waiting for you" tone="caution" size="small" icon={<HourglassEmpty />} />;
  };

  if (isLoading) return <LoadingState />;

  return (
    <>
      <RebateProgramsSection />
      <StoreRebateRequestsSection />
      <StoreSaleWindowsSection />
      <Alert severity="info" sx={{ mb: 2 }}>
        A vendor can send your store a private price sheet for a brand you are verified for. Review the rows, then accept or decline. An accepted sheet becomes your pricing for those products, above your own uploads and below your per-product overrides. It is never shared with other stores.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {!offers || offers.length === 0 ? (
        <EmptyState title="No vendor offers" description="Private sheets vendors send to your store show up here." size="compact" />
      ) : isMobile ? (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Stack divider={<Divider />}>
            {offers.map((offer) => {
              const open = expanded === offer.id;
              const pending = offer.orgApprovalStatus !== 'approved' && offer.status !== 'rejected' && offer.status !== 'duplicate';
              return (
                <Box key={offer.id}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2">{offer.vendor}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {offer.targetBrandName || '—'} · {offer.appliesTo || 'All locations'}
                        </Typography>
                      </Box>
                      {offerChip(offer)}
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 1.5 }}>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>File</Typography>
                        <Typography variant="body2" noWrap>{offer.fileName}</Typography>
                      </Box>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Rows</Typography>
                        <Typography variant="body2">{offer.recordsFound}</Typography>
                      </Box>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Received</Typography>
                        <Typography variant="body2">{offer.createdAt ? new Date(offer.createdAt).toLocaleDateString() : '—'}</Typography>
                      </Box>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={open ? <ExpandLess /> : <ExpandMore />}
                        onClick={() => setExpanded(open ? null : offer.id)}
                      >
                        {open ? 'Hide rows' : 'Show rows'}
                      </Button>
                      {pending && (
                        <>
                          <Button size="small" variant="contained" disabled={decide.isPending} onClick={() => decide.mutate({ id: offer.id, action: 'org-approve' })}>Accept</Button>
                          <Button size="small" color="inherit" variant="outlined" disabled={decide.isPending} onClick={() => decide.mutate({ id: offer.id, action: 'org-decline' })}>Decline</Button>
                        </>
                      )}
                    </Stack>
                  </Box>
                  <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 2, pb: 2, bgcolor: 'action.hover' }}>
                      {!offerRecords || offerRecords.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No rows to show.</Typography>
                      ) : (
                        <Paper variant="outlined">
                          <Stack divider={<Divider />}>
                            {offerRecords.map((rec) => (
                              <Box key={rec.id} sx={{ p: 1.5 }}>
                                <Typography variant="body2" fontWeight={500}>{rec.record.model}</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 0.75 }}>
                                  <Box>
                                    <Typography component="span" sx={mobileLabelSx}>MAP</Typography>
                                    <Typography variant="body2">{rec.record.mapPrice != null ? `$${Number(rec.record.mapPrice).toFixed(2)}` : '—'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography component="span" sx={mobileLabelSx}>Rebate</Typography>
                                    <Typography variant="body2">{rec.record.instantRebate != null ? `$${Number(rec.record.instantRebate).toFixed(2)}` : '—'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography component="span" sx={mobileLabelSx}>Dealer cost</Typography>
                                    <Typography variant="body2">{rec.record.dealerPrice != null ? `$${Number(rec.record.dealerPrice).toFixed(2)}` : '—'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography component="span" sx={mobileLabelSx}>Trade credit</Typography>
                                    <Typography variant="body2">{rec.record.tradeCredit != null ? `$${Number(rec.record.tradeCredit).toFixed(2)}` : '—'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography component="span" sx={mobileLabelSx}>Effective</Typography>
                                    <Typography variant="body2">{rec.record.effectiveDate ? new Date(rec.record.effectiveDate).toLocaleDateString() : '—'}</Typography>
                                  </Box>
                                  <Box>
                                    <Typography component="span" sx={mobileLabelSx}>Ends</Typography>
                                    <Typography variant="body2">{rec.record.endDate ? new Date(rec.record.endDate).toLocaleDateString() : '—'}</Typography>
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </Paper>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Vendor</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Applies to</TableCell>
                <TableCell>File</TableCell>
                <TableCell align="right">Rows</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Received</TableCell>
                <TableCell align="right">Decision</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {offers.map((offer) => {
                const open = expanded === offer.id;
                const pending = offer.orgApprovalStatus !== 'approved' && offer.status !== 'rejected' && offer.status !== 'duplicate';
                return (
                  <React.Fragment key={offer.id}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton size="small" aria-label={open ? 'Hide rows' : 'Show rows'} onClick={() => setExpanded(open ? null : offer.id)}>
                          {open ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{offer.vendor}</TableCell>
                      <TableCell>{offer.targetBrandName || '—'}</TableCell>
                      <TableCell>{offer.appliesTo || 'All locations'}</TableCell>
                      <TableCell>{offer.fileName}</TableCell>
                      <TableCell align="right">{offer.recordsFound}</TableCell>
                      <TableCell>{offerChip(offer)}</TableCell>
                      <TableCell>{offer.createdAt ? new Date(offer.createdAt).toLocaleDateString() : '—'}</TableCell>
                      <TableCell align="right">
                        {pending && (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Button size="small" variant="contained" disabled={decide.isPending} onClick={() => decide.mutate({ id: offer.id, action: 'org-approve' })}>Accept</Button>
                            <Button size="small" color="inherit" disabled={decide.isPending} onClick={() => decide.mutate({ id: offer.id, action: 'org-decline' })}>Decline</Button>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0, bgcolor: 'action.hover' }}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5 }}>
                              {!offerRecords || offerRecords.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No rows to show.</Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Model</TableCell>
                                      <TableCell align="right">MAP</TableCell>
                                      <TableCell align="right">Rebate</TableCell>
                                      <TableCell align="right">Dealer cost</TableCell>
                                      <TableCell align="right">Trade credit</TableCell>
                                      <TableCell>Effective</TableCell>
                                      <TableCell>Ends</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {offerRecords.map((rec) => (
                                      <TableRow key={rec.id}>
                                        <TableCell>{rec.record.model}</TableCell>
                                        <TableCell align="right">{rec.record.mapPrice != null ? `$${Number(rec.record.mapPrice).toFixed(2)}` : '—'}</TableCell>
                                        <TableCell align="right">{rec.record.instantRebate != null ? `$${Number(rec.record.instantRebate).toFixed(2)}` : '—'}</TableCell>
                                        <TableCell align="right">{rec.record.dealerPrice != null ? `$${Number(rec.record.dealerPrice).toFixed(2)}` : '—'}</TableCell>
                                        <TableCell align="right">{rec.record.tradeCredit != null ? `$${Number(rec.record.tradeCredit).toFixed(2)}` : '—'}</TableCell>
                                        <TableCell>{rec.record.effectiveDate ? new Date(rec.record.effectiveDate).toLocaleDateString() : '—'}</TableCell>
                                        <TableCell>{rec.record.endDate ? new Date(rec.record.endDate).toLocaleDateString() : '—'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
          </TableContainer>
        </Paper>
      )}
    </>
  );
};

/**
 * Until the store says how an instant rebate should reach its customers, every
 * rebate here syncs on a choice nobody made. Not dismissible: the sync keeps
 * using the old behaviour until the store answers on Store Settings.
 */
const RebateTreatmentPrompt: React.FC = () => {
  const { t } = useAppTranslation();
  const { enabled } = useOrgFeatureFlag('rebate_checkout_treatment');

  const { data } = useQuery<{ chosen: boolean } | null>({
    queryKey: ['store-rebate-treatment'],
    queryFn: async () => {
      const res = await makeApiCall('/store/settings/rebate-treatment', { headers: await getAuthHeaders() });
      if (!res.ok) return null;
      return (await res.json())?.data ?? null;
    },
    enabled,
  });

  if (!enabled || !data || data.chosen) return null;

  return (
    <Alert
      severity="info"
      sx={{ mb: 3, borderRadius: 1.5 }}
      action={
        <Button color="inherit" size="small" component={RouterLink} to="/app/store-settings">
          {t('store.rebateTreatment.alertAction')}
        </Button>
      }
    >
      {t('store.rebateTreatment.alert')}
    </Alert>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────────────

export const MapPricingDataViewPage: React.FC = () => {
  const [urlTab, setActiveTab] = useUrlTab(['platform', 'uploads', 'offers', 'email', 'pending-changes'] as const, 'platform');
  const { t } = useAppTranslation();
  // Store approval step for incoming price changes (flag `price_change_review`); null while the flag is off.
  const pendingChanges = usePendingPriceChangeCount();
  const activeTab = urlTab === 'pending-changes' && pendingChanges === null ? 'platform' : urlTab;

  return (
    <PageContainer>
      <PageHeader
        title="Vendor pricing & rebates"
        description="View MAP pricing, rebates, and MSRP data across all vendors"
      />

      <RebateTreatmentPrompt />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Platform pricing" value="platform" />
          <Tab label="My uploads" icon={<CloudUpload sx={{ fontSize: 16 }} />} iconPosition="end" value="uploads" />
          <Tab label="Vendor offers" icon={<LocalOffer sx={{ fontSize: 16 }} />} iconPosition="end" value="offers" />
          <Tab label="Email ingestion" icon={<Email sx={{ fontSize: 16 }} />} iconPosition="end" value="email" />
          {pendingChanges !== null && (
            <Tab
              value="pending-changes"
              label={
                <Badge badgeContent={pendingChanges} color="warning" max={99} sx={{ '& .MuiBadge-badge': { right: -14 } }}>
                  {t('store.priceChangeReview.tab')}
                </Badge>
              }
              sx={{ pr: pendingChanges > 0 ? 4 : undefined }}
            />
          )}
        </Tabs>
      </Box>

      {activeTab === 'platform' && <PlatformPricingTab />}
      {activeTab === 'uploads' && <MyUploadsTab />}
      {activeTab === 'offers' && <VendorOffersTab />}
      {activeTab === 'email' && <EmailIngestionTab />}
      {activeTab === 'pending-changes' && <PendingPriceChangesTab />}
    </PageContainer>
  );
};

export default MapPricingDataViewPage;
